import React, { useState } from 'react'
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useForm } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import { format, subDays } from 'date-fns'
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined'
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined'
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined'
import FormProvider from '../../../common/hook-form/FormProvider'
import RHFTextField from '../../../common/hook-form/RHFTextField'
import RHFDateRangePickerField from '../../../common/hook-form/RHFDateRangePickerField'
import { MerchantFilterCached } from '../../../common/ProshipFormComponents'
import ProzoDataGrid from '../../../common/ProzoDataGrid'
import MUIModal from '../../MuiModal'
import { apiConstants, post } from '../../../common/constants'

const PAGE_SIZE = 20

function formatDateTime(val) {
  if (!val) return '—'
  try { return format(new Date(val), 'dd MMM yyyy, HH:mm:ss') } catch { return val }
}

// ── Detail Modal ─────────────────────────────────────────────────────────────
function LoginDetailModal({ row }) {
  if (!row) return null
  const pastLogins = row.logins?.pastLogins || []
  const lastLogin = row.logins?.lastLogin
  const integrations = Object.entries(row.integrationAuthTimestamps || {})

  const initials = row.fullName
    ? row.fullName.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : row.email?.[0]?.toUpperCase() || '?'

  return (
    <Box>
      {/* User card */}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 2, mb: 2.5, p: 2,
          bgcolor: '#F0F7FF', borderRadius: 2, border: '1px solid #C5D8F8',
        }}
      >
        <Avatar sx={{ width: 52, height: 52, bgcolor: 'primary.main', fontSize: 20, fontWeight: 700 }}>
          {initials}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap>{row.fullName || '—'}</Typography>
          <Typography variant="body2" color="text.secondary" noWrap>{row.email}</Typography>
        </Box>
        <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
          <Typography variant="h4" fontWeight={800} color="primary.main" lineHeight={1}>{pastLogins.length}</Typography>
          <Typography variant="caption" color="text.secondary">logins</Typography>
        </Box>
      </Box>

      {/* Last login */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, px: 2, py: 1.25, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
        <AccessTimeOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">Last Login</Typography>
          <Typography variant="body2" fontWeight={500}>{formatDateTime(lastLogin)}</Typography>
        </Box>
      </Box>

      {/* Past logins list */}
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        Past Logins ({pastLogins.length})
      </Typography>
      <Box
        sx={{
          maxHeight: 200, overflowY: 'auto', borderRadius: 1.5,
          border: '1px solid', borderColor: 'divider', mb: 2,
        }}
      >
        {pastLogins.length === 0 ? (
          <Typography variant="body2" sx={{ p: 2, color: 'text.disabled' }}>No past logins recorded</Typography>
        ) : (
          [...pastLogins].reverse().map((ts, i) => (
            <Box
              key={i}
              sx={{
                px: 2, py: 0.875,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                borderBottom: i < pastLogins.length - 1 ? '1px solid' : 'none',
                borderColor: 'divider',
                '&:hover': { bgcolor: 'grey.50' },
                transition: 'background 0.15s',
              }}
            >
              <Typography variant="body2">{formatDateTime(ts)}</Typography>
              <Chip label={`#${pastLogins.length - i}`} size="small" variant="outlined" sx={{ height: 20, fontSize: '11px', color: 'text.secondary' }} />
            </Box>
          ))
        )}
      </Box>

      {/* Integration auths */}
      {integrations.length > 0 && (
        <>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Integration Auths ({integrations.length})
          </Typography>
          <Stack spacing={1}>
            {integrations.map(([name, detail]) => (
              <Box
                key={name}
                sx={{
                  p: 1.5, borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: detail.status === 'SUCCESSFUL' ? 'success.light' : 'error.light',
                  bgcolor: detail.status === 'SUCCESSFUL' ? '#F0FAF0' : '#FFF5F5',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>{name}</Typography>
                  <Chip label={detail.status} size="small" color={detail.status === 'SUCCESSFUL' ? 'success' : 'error'} />
                </Box>
                <Typography variant="body2" color="text.secondary">{formatDateTime(detail.timestamp)}</Typography>
                {detail.ipAddress && (
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', display: 'block', mt: 0.25 }}>
                    {detail.ipAddress}
                  </Typography>
                )}
                {detail.message && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>{detail.message}</Typography>
                )}
              </Box>
            ))}
          </Stack>
        </>
      )}
    </Box>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────
// view: 'login' | 'password'
export default function LoginPasswordTab({ view = 'login' }) {
  const { enqueueSnackbar } = useSnackbar()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(PAGE_SIZE)
  const [rowCount, setRowCount] = useState(0)
  const [lastFormData, setLastFormData] = useState(null)
  const [detailRow, setDetailRow] = useState(null)

  const methods = useForm({
    defaultValues: {
      merchant: null,
      email: '',
      fromDate: subDays(new Date(), 7),
      toDate: new Date(),
    },
  })

  const { handleSubmit, reset } = methods

  const handleReset = () => {
    reset({ merchant: null, email: '', fromDate: subDays(new Date(), 7), toDate: new Date() })
    setRows([])
    setSubmitted(false)
    setPage(0)
    setLastFormData(null)
  }

  const buildPayload = (formData, offset = 0, limit = PAGE_SIZE) => {
    const payload = { offset, limit }
    if (formData.merchant) payload.merchantId = formData.merchant
    if (formData.email?.trim()) payload.email = formData.email.trim()
    if (formData.fromDate) payload.fromDate = format(new Date(formData.fromDate), 'dd-MM-yyyy')
    if (formData.toDate) payload.toDate = format(new Date(formData.toDate), 'dd-MM-yyyy')
    return payload
  }

  const fetchData = (formData, offset = 0, limit = PAGE_SIZE) => {
    const endpoint = view === 'login' ? apiConstants.AUDIT_LOGIN_HISTORY : apiConstants.AUDIT_PASSWORD_CHANGE
    const payload = buildPayload(formData, offset, limit)
    setLoading(true)
    post(endpoint, payload)
      .then((res) => {
        const raw = res?.data
        const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : []
        setRows(list.map((r, i) => ({ ...r, id: i })))
        setRowCount(offset + list.length + (list.length >= limit ? 1 : 0))
        setSubmitted(true)
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || 'Failed to fetch data'
        enqueueSnackbar(msg, { variant: 'error' })
      })
      .finally(() => setLoading(false))
  }

  const onSubmit = (formData) => {
    setPage(0)
    setLastFormData(formData)
    fetchData(formData, 0, sizePerPage)
  }

  const handlePageChange = (newPage) => {
    setPage(newPage)
    if (lastFormData) fetchData(lastFormData, newPage * sizePerPage, sizePerPage)
  }

  const handleSizeChange = (newSize) => {
    setSizePerPage(newSize)
    setPage(0)
    if (lastFormData) fetchData(lastFormData, 0, newSize)
  }

  // ── Login History columns ─────────────────────────────────────────────────
  const loginColumns = [
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.5,
      renderCell: (p) => <Typography variant="body2">{p.value || '—'}</Typography>,
    },
    {
      field: 'fullName',
      headerName: 'Full Name',
      flex: 1.2,
      renderCell: (p) => <Typography variant="body2">{p.value || '—'}</Typography>,
    },
    {
      field: 'logins',
      headerName: 'Last Login',
      flex: 1.2,
      renderCell: (p) => (
        <Typography variant="body2">{formatDateTime(p.value?.lastLogin)}</Typography>
      ),
    },
    {
      field: 'loginCount',
      headerName: 'Logins (period)',
      flex: 0.9,
      renderCell: (p) => {
        const count = p.row?.logins?.pastLogins?.length || 0
        return <Chip label={count} size="small" color="primary" variant="outlined" />
      },
    },
    {
      field: 'integrationAuthTimestamps',
      headerName: 'Integrations',
      flex: 2,
      sortable: false,
      renderCell: (p) => {
        const entries = Object.entries(p.value || {})
        if (!entries.length) return <Typography variant="body2">—</Typography>
        return (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {entries.map(([name, detail]) => (
              <Chip
                key={name}
                label={name}
                size="small"
                color={detail.status === 'SUCCESSFUL' ? 'success' : 'error'}
                variant="outlined"
              />
            ))}
          </Stack>
        )
      },
    },
    {
      field: 'actions',
      headerName: '',
      flex: 0.5,
      sortable: false,
      renderCell: (p) => (
        <Tooltip title="View full details">
          <IconButton size="small" onClick={() => setDetailRow(p.row)}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ]

  // ── Password Change columns ───────────────────────────────────────────────
  const passwordColumns = [
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.5,
      renderCell: (p) => <Typography variant="body2">{p.value || '—'}</Typography>,
    },
    {
      field: 'fullName',
      headerName: 'Full Name',
      flex: 1.2,
      renderCell: (p) => <Typography variant="body2">{p.value || '—'}</Typography>,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      renderCell: (p) => (
        <Chip
          label={p.value || '—'}
          size="small"
          color={p.value === 'ACTIVE' ? 'success' : p.value === 'RESET' ? 'warning' : 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'lastPasswordChangeTime',
      headerName: 'Last Password Change',
      flex: 1.5,
      renderCell: (p) => (
        <Typography variant="body2">
          {p.value ? formatDateTime(p.value) : <span style={{ color: '#aaa' }}>Never changed</span>}
        </Typography>
      ),
    },
  ]

  const columns = view === 'login' ? loginColumns : passwordColumns

  return (
    <Box>
      {/* Filter card */}
      <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: 'none' }}>
        <Box
          sx={{
            px: 2.5, py: 1.5,
            display: 'flex', alignItems: 'center', gap: 1,
            bgcolor: '#fafafa', borderBottom: '1px solid', borderColor: 'divider',
          }}
        >
          <FilterAltOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'text.primary' }}>Search Filters</Typography>
        </Box>
        <CardContent>
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <MerchantFilterCached fullWidth />
              </Grid>
              <Grid item xs={12} md={3}>
                <RHFTextField name="email" label="Email" placeholder="user@example.com" />
              </Grid>
              <Grid item xs={12} md={3}>
                <RHFDateRangePickerField label="Date Range" />
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button type="submit" variant="contained" size="medium" disabled={loading} startIcon={<SearchOutlinedIcon />}>
                    Search
                  </Button>
                  <Button type="button" variant="outlined" size="medium" onClick={handleReset} startIcon={<RestartAltOutlinedIcon />}>
                    Reset
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </FormProvider>
        </CardContent>
      </Card>

      {/* Results */}
      {submitted && (
        <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: 'none' }}>
          <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#fafafa' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {view === 'login'
                ? <LoginOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                : <LockResetOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
              <Typography variant="subtitle2" fontWeight={600}>
                {view === 'login' ? 'Login History' : 'Password Changes'}
              </Typography>
            </Box>
            <Chip label={`${rows.length} records`} size="small" color="primary" variant="outlined" />
          </Box>
          {rows.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <InboxOutlinedIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1.5 }} />
              <Typography variant="subtitle1" color="text.secondary" fontWeight={500}>No records found</Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>Try adjusting your filters and search again</Typography>
            </Box>
          ) : (
            <CardContent sx={{ px: 0, pb: '0 !important' }}>
              <ProzoDataGrid
                rows={rows}
                columns={columns}
                rowCount={rowCount}
                page={page}
                setPage={handlePageChange}
                sizePerPage={sizePerPage}
                setSizePerPage={handleSizeChange}
                paginationMode="server"
                disableColumnMenu
                disableColumnFilter
              />
            </CardContent>
          )}
        </Card>
      )}

      {/* Detail Modal */}
      <MUIModal
        open={Boolean(detailRow)}
        handleClose={() => setDetailRow(null)}
        title="User Login Details"
        maxWidth="sm"
      >
        <LoginDetailModal row={detailRow} />
      </MUIModal>
    </Box>
  )
}
